// providers/salesforce.ts
import * as Api from '@ViewModels';
import { ConfigPath } from './constants';
import { dynamicSection, lockableSelect, provider, searchableSelect, toggle } from './helpers';

export const salesforceConfig = provider(Api.IntegrationProvider.Salesforce, {
	displayName: 'Salesforce',

	main: {
		writebacks: true,
		fields: [
			toggle('preferNicknameAsFirstName', {
				label: 'Prefer Nickname as First Name',
				subtitle: 'Use the contact nickname from Salesforce as the first name in Levitate',
			}),
			lockableSelect('contactRecordType', ConfigPath.AVAILABLE_RECORD_TYPES, {
				label: 'Contact Record Type',
				subtitle: 'Controls which Salesforce record type to use for contact syncing (cannot be changed once set)',
			}),
			searchableSelect('deceasedField', ConfigPath.ALL_AVAILABLE_FIELDS, {
				label: 'Deceased Indicator',
				subtitle: 'Maps a Salesforce field to mark contacts as deceased',
			}),
		],
	},

	keyFacts: {
		birthdayDisplay: false,
		dateFields: true,
		customDateFields: true,
		extraFields: [
			// Parent-account date fields as key facts. hideWhenNoOptions: the config map is empty for
			// PersonAccount orgs, so the picker self-hides rather than showing an empty selector.
			searchableSelect('enabledAccountDateFields', ConfigPath.AVAILABLE_ACCOUNT_DATE_FIELDS, {
				label: 'Account Date Fields',
				subtitle: 'Select parent account date fields to be used as key facts',
				multiple: true,
				hideWhenNoOptions: true,
			}),
		],
	},

	tags: {
		coreTags: false, // Salesforce uses field-based tags instead
		dynamicTags: true, // Uses availableFields -> enabledFields
		customFields: true,
		customSections: [
			{
				id: 'campaignTags',
				label: 'Campaign Tags',
				subtitle: 'Tag contacts based on their Salesforce Campaign membership',
				options: [{ id: 'nameEnabled', label: 'Name' }],
				storageType: 'nested',
				settingsKey: 'campaignTagSettings',
			},
			dynamicSection('interestTags', ConfigPath.AVAILABLE_INTEREST_TAGS, {
				label: 'Interest Tags',
				subtitle: 'Tag contacts based on their Salesforce interest tag assignments',
				settingsKey: 'interestTagSettings',
				enabledKey: 'excludedTags',
				idKey: 'tagId',
				labelKey: 'tagName',
				descriptionKey: 'categoryNames',
				enableToggle: true,
				enabledToggleKey: 'enabled',
				manageButtonLabel: 'Exclude Tags',
			}),
			dynamicSection('topics', ConfigPath.AVAILABLE_TOPICS, {
				label: 'Topics',
				subtitle: 'Tag contacts based on their Salesforce topic assignments',
				settingsKey: 'topicSettings',
				enabledKey: 'excludedTopics',
				idKey: 'topicId',
				labelKey: 'topicName',
				enableToggle: true,
				enabledToggleKey: 'enabled',
				manageButtonLabel: 'Exclude Topics',
			}),
		],
		extraFields: [
			// Parent-account fields (standard or custom) as contact tags. hideWhenNoOptions: the config map
			// is empty for PersonAccount orgs, so the picker self-hides rather than showing an empty selector.
			searchableSelect('enabledAccountFields', ConfigPath.AVAILABLE_ACCOUNT_FIELDS, {
				label: 'Account Fields',
				subtitle: 'Select parent account fields (standard or custom) to be used as contact tags',
				multiple: true,
				hideWhenNoOptions: true,
			}),
			searchableSelect('unsubscribeFields', ConfigPath.ALL_AVAILABLE_FIELDS, {
				label: 'Unsubscribe Indicators',
				subtitle: 'Maps Salesforce fields to mark contacts as unsubscribed',
				multiple: true,
			}),
		],
	},
});
